import { SigninFailed, useSessionStore } from '@rentsales/shared'
import { DateTime } from 'luxon'
import { createRouter, createWebHistory } from 'vue-router'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeView.vue'),
      redirect: '/map',
      children: [
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue')
        },
        {
          path: '/map',
          name: 'map',
          component: () => import('@/views/LeafletMapView.vue')
        },
        {
          path: '/address-details/:id?',
          name: 'address-details',
          component: () => import('@/views/AddressDetails.vue'),
          props: (route) => ({
            id: route.params.id,
            navigateBackAfterCompletion: route.query.navigateBackAfterCompletion === 'true'
          })
        },
        {
          path: 'potentials/:id',
          name: 'contact-potentials',
          component: () => import('@/views/ContactPotentialsView.vue'),
          props: (route) => ({ contactId: route.params.id })
        },
        {
          path: '/questionnaires',
          name: 'questionnaires',
          component: () => import('@/views/QuestionnairesView.vue'),
          props: (route) => ({ date: route.query.date ? DateTime.fromISO(route.query.date as string).toJSDate() : new Date() })
        },
        {
          path: '/absences',
          name: 'absences',
          component: () => import('@/views/AbsencesView.vue')
        },
        {
          path: '/contact/:id',
          name: 'contact-overview',
          component: () => import('@/views/ContactOverviewView.vue'),
          props: (route) => ({
            id: route.params.id,
            navigateBackAfterCompletion: route.query.navigateBackAfterCompletion === 'true',
            returnDate: route.query.returnDate ? DateTime.fromISO(route.query.returnDate as string).toJSDate() : undefined
          }),
          children: [
            {
              path: 'visit/:visitId',
              name: 'visit-overview',
              component: () => import('@/views/ContactVisitView.vue'),
              props: (route) => ({ contactId: route.params.id, visitId: route.params.visitId })
            }
          ]
        },
        {
          path: '/contact/:id/previous-visits',
          name: 'visit-previous-visits',
          component: () => import('@/views/PreviousVisitsView.vue'),
          props: true
        },
        {
          path: '/visit/previous/:visitId',
          alias: '/visit/:visitId/readonly',
          name: 'visit-previous-visit',
          component: () => import('@/views/PreviousVisitView.vue'),
          props: (route) => ({ id: route.params.visitId })
        },
        {
          path: '/visit/:id/submissions',
          name: 'visit-submissions',
          component: () => import('@/views/VisitSubmissionsView.vue'),
          props: (route) => ({ id: route.params.id, navigateBackAfterCompletion: route.query.navigateBackAfterCompletion === 'true' })
        },
        {
          path: '/end-of-day/:date',
          name: 'end-of-day',
          component: () => import('@/views/EndOfDaySubmissionView.vue'),
          props: (route) => ({ date: DateTime.fromISO(route.params.date as string).toJSDate() })
        },
        {
          path: '/tasks',
          name: 'tasks',
          component: () => import('@/views/CreateTaskView.vue')
        }
      ],
      meta: {
        authenticated: true
      }
    },
    {
      path: '/signin-redirect',
      beforeEnter: async () => {
        const sessionStore = useSessionStore()
        await sessionStore.initSession()
        return { name: 'home' }
      },
      component: () => {}
    },
    {
      path: '/signin',
      name: 'signin',
      component: () => import('@/views/SignInView.vue')
    },
    {
      path: '/signin-failed',
      name: 'signin-failed',
      component: SigninFailed
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ]
})

router.beforeEach((to, _from, next) => {
  const sessionStore = useSessionStore()
  // is authentication required
  if (to.matched.some((record) => record.meta.authenticated)) {
    if (!sessionStore.isAuthenticated) {
      next({ name: 'signin' })
      return
    }
  } else if (to.matched.some((record) => record.meta.anonymous) && sessionStore.isAuthenticated) {
    next({ name: 'home' })
    return
  }
  next()
})

export default router
